import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Building2, Users, DollarSign, TrendingUp, Shield, 
  Bot, Cpu, AlertTriangle, Lightbulb, BarChart3,
  ArrowRight, Zap, PiggyBank, Activity, GitBranch, Leaf
} from 'lucide-react';

/**
 * RoleBasedWidgets - Shows role-specific quick actions and insights
 * - Admin: Full overview + user management shortcuts
 * - Finance: P&L summaries, budget alerts, cost tracking
 * - Engineering: Resource optimization, anomalies, technical metrics
 */
const RoleBasedWidgets = ({ summary, anomalies, recommendations }) => {
  const { user, isAdmin, isFinance, isEngineering, canAccess } = useAuth();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Admin-specific widgets
  const AdminWidgets = () => (
    <Card className="border-rose-200 dark:border-rose-800 bg-rose-50 bg-gradient-to-br from-rose-50 to-white dark:from-rose-950/20 dark:to-slate-900" data-testid="admin-widgets">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/50">
            <Shield className="w-5 h-5 text-rose-600 dark:text-rose-400" />
          </div>
          <div>
            <CardTitle className="text-base">Admin Control Center</CardTitle>
            <CardDescription>Full platform access & management</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Link to="/cloud-pl">
            <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-700 transition-colors cursor-pointer">
              <Building2 className="w-4 h-4 text-rose-500 mb-1" />
              <p className="text-xs font-medium">Cloud P&L</p>
              <p className="text-[11px] text-slate-400">Board reports</p>
            </div>
          </Link>
          <Link to="/pilot">
            <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-700 transition-colors cursor-pointer">
              <Bot className="w-4 h-4 text-rose-500 mb-1" />
              <p className="text-xs font-medium">Auto Pilot</p>
              <p className="text-[11px] text-slate-400">Execute actions</p>
            </div>
          </Link>
          <Link to="/customer-pl">
            <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-700 transition-colors cursor-pointer">
              <Users className="w-4 h-4 text-rose-500 mb-1" />
              <p className="text-xs font-medium">Customer Margins</p>
              <p className="text-[11px] text-slate-400">Profitability</p>
            </div>
          </Link>
          <Link to="/settings">
            <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-rose-300 dark:hover:border-rose-700 transition-colors cursor-pointer">
              <Activity className="w-4 h-4 text-rose-500 mb-1" />
              <p className="text-xs font-medium">System Settings</p>
              <p className="text-[11px] text-slate-400">Configure</p>
            </div>
          </Link>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
          <span className="text-xs text-slate-400">Full admin access enabled</span>
          <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400 text-[11px]">
            Admin
          </Badge>
        </div>
      </CardContent>
    </Card>
  );

  // Finance-specific widgets
  const FinanceWidgets = () => (
    <Card className="border-sky-200 dark:border-sky-800 bg-sky-50 bg-gradient-to-br from-sky-50 to-white dark:from-sky-950/20 dark:to-slate-900" data-testid="finance-widgets">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-sky-100 dark:bg-sky-900/50">
            <DollarSign className="w-5 h-5 text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <CardTitle className="text-base">Finance Dashboard</CardTitle>
            <CardDescription>Financial metrics & reporting</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Key Financial Metrics */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <p className="text-[11px] text-slate-400 uppercase tracking-wider">MTD Spend</p>
            <p className="text-lg font-bold text-slate-900 dark:text-white">{formatCurrency(summary?.total_cost_mtd)}</p>
            <p className={`text-xs ${summary?.cost_change_percent >= 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
              {summary?.cost_change_percent >= 0 ? '+' : ''}{summary?.cost_change_percent}% vs last
            </p>
          </div>
          <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <p className="text-[11px] text-slate-400 uppercase tracking-wider">Savings Opp</p>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(summary?.potential_savings)}</p>
            <p className="text-xs text-slate-400">{recommendations?.length || 0} actions</p>
          </div>
        </div>
        
        {/* Quick Links */}
        <div className="flex gap-2">
          <Link to="/cloud-pl" className="flex-1">
            <Button variant="outline" size="sm" className="w-full text-xs gap-1 border-sky-200 hover:bg-sky-50 dark:border-sky-800 dark:hover:bg-sky-900/30">
              <Building2 className="w-3 h-3" />
              Cloud P&L
            </Button>
          </Link>
          <Link to="/tech-spend" className="flex-1">
            <Button variant="outline" size="sm" className="w-full text-xs gap-1 border-sky-200 hover:bg-sky-50 dark:border-sky-800 dark:hover:bg-sky-900/30">
              <BarChart3 className="w-3 h-3" />
              Tech Spend
            </Button>
          </Link>
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
          <Link to="/guardrails" className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1">
            View Budget Guardrails <ArrowRight className="w-3 h-3" />
          </Link>
          <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-400 text-[11px]">
            Finance
          </Badge>
        </div>
      </CardContent>
    </Card>
  );

  // Engineering-specific widgets
  const EngineeringWidgets = () => (
    <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/20 dark:to-slate-900" data-testid="engineering-widgets">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/50">
            <Cpu className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <CardTitle className="text-base">Engineering Hub</CardTitle>
            <CardDescription>Resource optimization & monitoring</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Alerts Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <p className="text-[11px] text-slate-400 uppercase">Anomalies</p>
            </div>
            <p className="text-lg font-bold text-slate-900 dark:text-white">{anomalies?.length || 0}</p>
            <p className="text-xs text-slate-400">Needs review</p>
          </div>
          <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb className="w-4 h-4 text-emerald-500" />
              <p className="text-[11px] text-slate-400 uppercase">Optimizations</p>
            </div>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{recommendations?.length || 0}</p>
            <p className="text-xs text-slate-400">Available</p>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="space-y-2">
          <Link to="/recommendations">
            <div className="p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors cursor-pointer flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium">View Recommendations</span>
              </div>
              <span className="text-sm font-bold text-emerald-600">{formatCurrency(summary?.potential_savings)}</span>
            </div>
          </Link>
          <Link to="/pilot">
            <div className="p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors cursor-pointer flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium">Auto Pilot Actions</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </div>
          </Link>
        </div>
        
        {/* Quick Links */}
        <div className="flex gap-2">
          <Link to="/bill-detective" className="flex-1">
            <Button variant="outline" size="sm" className="w-full text-xs gap-1 border-emerald-200 hover:bg-emerald-50 dark:border-emerald-800 dark:hover:bg-emerald-900/30">
              <GitBranch className="w-3 h-3" />
              Bill Detective
            </Button>
          </Link>
          <Link to="/time-machine" className="flex-1">
            <Button variant="outline" size="sm" className="w-full text-xs gap-1 border-emerald-200 hover:bg-emerald-50 dark:border-emerald-800 dark:hover:bg-emerald-900/30">
              <TrendingUp className="w-3 h-3" />
              Time Machine
            </Button>
          </Link>
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
          <Link to="/anomalies" className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
            View All Anomalies <ArrowRight className="w-3 h-3" />
          </Link>
          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400 text-[11px]">
            Engineering
          </Badge>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="role-based-widgets">
      {/* Show role-specific widget */}
      {isAdmin && <AdminWidgets />}
      {isFinance && <FinanceWidgets />}
      {isEngineering && <EngineeringWidgets />}
      
      {/* ESG Widget - shown to Admin and Finance */}
      {(isAdmin || isFinance) && canAccess('cloud_pl') && (
        <Card className="border-green-200 dark:border-green-800 bg-green-50 bg-gradient-to-br from-green-50 to-white dark:from-green-950/20 dark:to-slate-900" data-testid="esg-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
                <Leaf className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <CardTitle className="text-base">Sustainability</CardTitle>
                <CardDescription>Carbon + ESG metrics</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">--/100</p>
                <p className="text-xs text-slate-400">ESG Score</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-slate-900 dark:text-white">--</p>
                <p className="text-xs text-slate-400">Carbon reduction</p>
              </div>
            </div>
            <Link to="/carbon">
              <Button variant="outline" size="sm" className="w-full text-xs gap-1 border-green-200 hover:bg-green-50 dark:border-green-800 dark:hover:bg-green-900/30">
                <Leaf className="w-3 h-3" />
                View Carbon + ESG Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
      
      {/* Savings Tracker - shown to all roles */}
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/20 dark:to-slate-900" data-testid="savings-widget">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50">
              <PiggyBank className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-base">Savings Tracker</CardTitle>
              <CardDescription>Implementation progress</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{formatCurrency(summary?.potential_savings * 0.6)}</p>
              <p className="text-xs text-slate-400">Realized savings</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-slate-900 dark:text-white">{formatCurrency(summary?.potential_savings)}</p>
              <p className="text-xs text-slate-400">Total opportunity</p>
            </div>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 mb-2">
            <div className="bg-amber-500 h-2 rounded-full" style={{ width: '60%' }}></div>
          </div>
          <Link to="/savings">
            <Button variant="outline" size="sm" className="w-full text-xs gap-1 border-amber-200 hover:bg-amber-50 dark:border-amber-800 dark:hover:bg-amber-900/30">
              <PiggyBank className="w-3 h-3" />
              View Savings Details
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleBasedWidgets;
